import {
  NO_LENS_VISUALIZER_LENS_KEY,
  type VisualizerLens,
} from "./uploadedConversationsVisualizerDataset"

const LISTED_GROUP_LIMIT = 3
const FAILURES_LENS_KEY = "failures"

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

/**
 * The plate's name and the takeaway a sighted reader gets from it: how many
 * conversations it draws and which groups are largest. Each dot is a
 * conversation that the page's conversation list also offers, and each group
 * is selectable beside the plate, so this text is the plate's alternative.
 */
export function describeVisualizerLens(lens: VisualizerLens): {
  label: string
  summary: string
} {
  const conversationIds = new Set(
    lens.bucketAssignments.flatMap((assignment) =>
      assignment.conversations.map((conversation) => conversation.id),
    ),
  )
  // The failures lens draws one dot per issue, and a conversation can have
  // several, so it counts issues and names how many conversations they span.
  const countsIssues = lens.key === FAILURES_LENS_KEY
  const dotCount = lens.bucketAssignments.reduce(
    (sum, assignment) => sum + assignment.conversations.length,
    0,
  )
  const conversations = countsIssues
    ? `${plural(dotCount, "issue")} across ${plural(conversationIds.size, "conversation")}`
    : plural(conversationIds.size, "conversation")

  if (lens.key === NO_LENS_VISUALIZER_LENS_KEY) {
    return {
      label: "Conversation map",
      summary: `${conversations}. Select a lens to group them.`,
    }
  }

  const groups = lens.bucketAssignments
    .filter((assignment) => assignment.conversations.length > 0)
    .sort(
      (left, right) => right.conversations.length - left.conversations.length,
    )
  const largest = groups
    .slice(0, LISTED_GROUP_LIMIT)
    .map(
      (assignment) =>
        `${assignment.label} (${assignment.conversations.length})`,
    )
    .join(", ")

  return {
    label: `Conversation map: ${lens.title}`,
    summary:
      groups.length === 0
        ? `${conversations}; none are grouped.`
        : `${conversations} in ${plural(groups.length, "group")}. Largest: ${largest}.`,
  }
}
